import React, { createContext, useContext, useState, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { computeFabStackIndex, computeFabStackOrder } from '../lib/fabStack';

/**
 * FabStackContext (#1696) — coordinates floating-action-button (FAB)
 * clusters, keyed by an arbitrary `corner` string, so multiple FABs sharing
 * one physical corner stack/fan in a deterministic order instead of
 * overlapping or fighting over ad-hoc CSS offsets.
 *
 * CORNER-AWARE (the actual fix over the mega62 predecessor): the registry is
 * `{ [corner]: { [id]: { priority, ...meta } } }`, not a single flat
 * bottom-right-only map. Two corners are wired up today (see lib/fabStack.js
 * for the full rationale):
 *   - 'menu'  — the consolidated fan-open FAB menu (FabMenu.jsx), docked at
 *     the existing top-left chat dock. Registrants pass a `meta` descriptor
 *     (icon/label/onClick/testId/badge) so FabMenu can render each item
 *     without importing every consumer component.
 *   - 'coach' — the debrief coach FAB's own corner (bottom-right), kept
 *     deliberately OUT of the 'menu' corner per the #1696 decision. CoachBubble
 *     needs no `meta` (it renders itself), so it registers with none.
 *
 * This provider only decides POSITION (which slot / vertical index a FAB
 * occupies within ITS corner) plus, for 'menu' registrants, carries the
 * render descriptor FabMenu needs. It never decides visibility — each FAB
 * keeps its own facilitator/debrief/route checks and simply reports the
 * result as the `active` argument to useFabStackSlot. A FAB that isn't
 * currently eligible for a slot unregisters and consumes no space, so the
 * others close the gap automatically.
 *
 * No default-provider throw: a FAB rendered without a FabStackProvider
 * ancestor (e.g. a component test that renders it in isolation) degrades to
 * "alone in its corner" (index 0) rather than crashing — safe standalone
 * behaviour, and it keeps existing renderToStaticMarkup-based FAB tests
 * working without needing to wrap every one of them in a provider.
 */

const noop = () => {};
const EMPTY_CORNER = {};
const DEFAULT_CONTEXT = { corners: {}, register: noop, unregister: noop };

const FabStackContext = createContext(DEFAULT_CONTEXT);

/**
 * THE REGISTRY LIVES IN A REF; `corners` STATE ONLY PUBLISHES IT (#3652).
 *
 * register()/unregister() decide "did anything change?" against
 * `cornersRef.current` and call `setCorners(<next value>)` only when it did.
 * They never pass an updater FUNCTION to setCorners. Two reasons, both
 * measured under the vite dev server (StrictMode on) with `help` + `bugReport`
 * registering on /dashboard:
 *
 *   1. React 18 keeps a bailed-out (eager-state-equal) update in the hook's
 *      queue at the lane it was dispatched on. StrictMode re-runs every mount
 *      layout effect after the passive-effect flush, at DefaultLane, so each
 *      registrant's `unregister()` on its inactive mount (a no-op) left a
 *      DefaultLane update pending in the queue. The first SyncLane update that
 *      followed (a layout-effect registration) rendered at SyncLane, skipped
 *      those pending updates for insufficient priority, and froze the hook's
 *      base state at the pre-skip snapshot.
 *   2. From then on every SyncLane render REPLAYED the queued updater
 *      functions on that frozen snapshot. `register()`'s updater therefore saw
 *      `prevEntry === undefined` every time, built a new `corners` object
 *      every time, and every consumer re-rendered, re-ran the dep-less
 *      freshness effect and dispatched another updater: a SyncLane loop that
 *      starves the skipped DefaultLane updates forever ("Maximum update depth
 *      exceeded"). Production never double-invokes layout effects, so no
 *      DefaultLane no-op ever sits in the queue there.
 *
 * Reading the equality check from the ref instead of from the updater's `prev`
 * makes it independent of whatever base state a replay starts from: an
 * unchanged registration dispatches nothing at all (so no no-op update is ever
 * queued), and the updates that are dispatched are plain values, so a replay
 * from any base ends on the latest value. The ref is only written from layout
 * effects (never during render), which run only for committed trees.
 */
export function FabStackProvider({ children }) {
  const cornersRef = useRef({});
  const [corners, setCorners] = useState(cornersRef.current);

  const publish = useCallback((next) => {
    cornersRef.current = next;
    setCorners(next);
  }, []);

  const register = useCallback((corner, id, priority, meta) => {
    const prev = cornersRef.current;
    const prevCorner = prev[corner] || EMPTY_CORNER;
    const prevEntry = prevCorner[id];
    if (prevEntry && prevEntry.priority === priority && shallowMetaEqual(prevEntry.meta, meta)) {
      return;
    }
    publish({
      ...prev,
      [corner]: { ...prevCorner, [id]: { priority, meta } },
    });
  }, [publish]);

  const unregister = useCallback((corner, id) => {
    const prev = cornersRef.current;
    const prevCorner = prev[corner];
    if (!prevCorner || !(id in prevCorner)) return;
    const nextCorner = { ...prevCorner };
    delete nextCorner[id];
    publish({ ...prev, [corner]: nextCorner });
  }, [publish]);

  const value = useMemo(() => ({ corners, register, unregister }), [corners, register, unregister]);

  return (
    <FabStackContext.Provider value={value}>
      {children}
    </FabStackContext.Provider>
  );
}

function shallowMetaEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(k => a[k] === b[k]);
}

function priorityMapOf(cornerEntries) {
  const map = {};
  for (const id of Object.keys(cornerEntries || EMPTY_CORNER)) {
    map[id] = cornerEntries[id].priority;
  }
  return map;
}

/**
 * Registers a FAB into `corner`'s stack while `active` is true.
 *
 * @param {string} corner     which physical FAB cluster this belongs to
 *   (e.g. 'menu', 'coach') — see this file's header for the corners in use.
 * @param {string} id         stable, unique registrant id within `corner`
 *   (e.g. 'home', 'help', 'bugReport', 'askPraxis', 'coach')
 * @param {number} priority   lower = nearer the corner/trigger — see
 *   FAB_PRIORITY (lib/fabStack.js)
 * @param {boolean} active    this FAB's OWN visibility/eligibility result —
 *   the stack only positions, so pass whatever the FAB already computes for
 *   whether it renders/registers at all.
 * @param {object} [meta]     optional render descriptor for 'menu'-corner
 *   registrants (icon, label, ariaLabel, onClick, testId, badge) — FabMenu
 *   reads this back via useFabCornerEntries. Registrants that render
 *   themselves (e.g. CoachBubble in the 'coach' corner) can omit it.
 * @returns {{ index: number, style: (object|undefined) }}
 *   `style` spreads directly onto the FAB's fixed-position root element:
 *   `<div style={style}>` — it sets the `--fab-stack-index` CSS custom
 *   property that the FAB's own stylesheet turns into an offset via
 *   `calc(var(--fab-stack-base) + var(--fab-stack-index, 0) * var(--fab-stack-step))`.
 *   `undefined` while inactive (nothing to position).
 *
 * useLayoutEffect (not useEffect): registration must land before the
 * browser paints, matching the same reasoning as App.jsx's
 * --help-panel-width effect — otherwise a second or third FAB would paint
 * one frame at index 0 (unstacked) and visibly jump once a post-paint effect
 * corrected it.
 *
 * TWO layout effects, deliberately split (review-fix H_fabstack_1696, P1):
 *
 *   - LIFECYCLE effect: register on activation, unregister on
 *     deactivation/unmount. Its deps are [corner, id, priority, active] —
 *     `meta` is NOT one of them.
 *   - FRESHNESS effect: re-runs register() on every single render while
 *     `active`, with NO dependency array at all, so it can never skip a
 *     render where `meta`'s fields legitimately changed.
 *
 * WHY NOT ONE EFFECT WITH `meta` IN ITS DEPS (tried first, reverted — see
 * git history on this file for the broken version): most callers build
 * `meta` as a fresh object literal every render (`{ icon, label, onClick,
 * ... }`), so its OBJECT IDENTITY differs every render even when every field
 * inside it is unchanged. React's dependency array only compares by
 * Object.is per element — it cannot see "the fields are the same" — so
 * `meta` in a deps array reruns that effect's CLEANUP (unregister) on every
 * render. `unregister` deletes the corner's entry unconditionally; by the
 * time the immediately-following register() call read `prevEntry`, the entry
 * had just been deleted, so `prevEntry` was always undefined and
 * register()'s own shallow-equality bail-out (shallowMetaEqual, above) never
 * got a chance to fire — every render therefore produced a genuinely new
 * `corners` object, which re-rendered every consumer, which rebuilt a fresh
 * `meta` object, which reran the effect again. Measured by hand 2026-09-06:
 * infinite render loop ("Maximum update depth exceeded"), not merely wasted
 * renders.
 *
 * WHY THE FRESHNESS EFFECT IS SAFE WITH NO DEPS: it only ever calls
 * register(), never unregister() — there is no delete-then-readd step to
 * erase `prevEntry` first, so register()'s shallow-equality bail-out (which
 * compares actual FIELD VALUES, e.g. `a.onClick === b.onClick`, not `meta`'s
 * own object identity) genuinely gets to run on every call: it returns
 * WITHOUT dispatching any state update at all (since #3652 the check reads
 * the provider's ref, see FabStackProvider's note, so a replayed or
 * bailed-out update queue cannot make it think the entry is missing) —
 * unless a field's value really changed. This still requires every field that can vary to be
 * comparable by value or by a STABLE reference: primitives (label,
 * ariaLabel, testId, badge) compare correctly with no caller effort; a
 * module-level icon constant (every current registrant's pattern) does too.
 *
 * ROOT FIX (review-fix(#1696), round 3, P1 — codex): the paragraph above
 * used to end here with "`onClick` MUST be memoized at the call site" — that
 * was wrong in practice. App.jsx's FloatingHelpButton passes a brand-new
 * `onClick: () => openHelp()` arrow on every render (it was never migrated
 * to useCallback when H_fabstack_1696 shipped), so `a.onClick === b.onClick`
 * was false on every single render, register()'s bail-out never fired, and
 * the resulting real `corners` update re-rendered every 'menu'-corner
 * consumer — including this same registrant — which built a fresh onClick
 * closure again, looping until React's max-update-depth error. Requiring
 * every caller to remember useCallback is a caller-side patch on a
 * provider-side defect: the ROOT fix is below, inside useFabStackSlot
 * itself, so no registrant ever needs a stable callback again.
 */
export function useFabStackSlot(corner, id, priority, active, meta) {
  const { corners, register, unregister } = useContext(FabStackContext);
  const cornerEntries = corners[corner] || EMPTY_CORNER;

  // ROOT FIX (review-fix(#1696) round 3, P1): keep the caller's raw `meta`
  // in a ref, written synchronously on every render (plain assignment during
  // render, not in an effect — nothing reads metaRef.current during render,
  // only later when a click actually happens, so there is no tearing risk).
  // `onClick` is invoked THROUGH the ref via `stableOnClick`, whose own
  // identity never changes for the life of this hook instance (useCallback
  // with an empty deps array). That wrapper — never the caller's raw
  // onClick — is the value handed to register(), so register()'s
  // shallow-equality bail-out (shallowMetaEqual, above) always compares the
  // SAME onClick reference across renders no matter how many fresh closures
  // the caller recreates. This is what "no registrant needs a stable
  // callback" means: App.jsx's FloatingHelpButton passes a brand-new
  // `onClick: () => openHelp()` arrow every render and no longer loops.
  const metaRef = useRef(meta);
  metaRef.current = meta;

  const stableOnClick = useCallback((...args) => metaRef.current?.onClick?.(...args), []);

  // Every other field (icon/label/ariaLabel/testId/badge) is passed through
  // as the caller's own value — those are expected to be primitives or
  // module-level constants (see this function's docstring), and
  // shallowMetaEqual still compares them by VALUE, so a real content change
  // (e.g. BugReporter's `badge: showIntro` flipping) still produces a real
  // state update and FabMenu still re-renders with the current value — only
  // `onClick`'s reference churn is absorbed here.
  const stableMeta = useMemo(() => {
    if (!meta) return meta;
    return { ...meta, onClick: stableOnClick };
  }, [meta, stableOnClick]);

  // Lifecycle: register on activation, unregister on deactivation/unmount.
  useLayoutEffect(() => {
    if (!active) {
      unregister(corner, id);
      return undefined;
    }
    register(corner, id, priority, stableMeta);
    return () => unregister(corner, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corner, id, priority, active]);

  // Freshness (review-fix H_fabstack_1696, P1; onClick indirection added in
  // review-fix(#1696) round 3): keeps `meta` current for as long as the
  // registrant stays active, even when only `meta`'s fields change. See this
  // function's docstring above for why this MUST be a second, dep-less
  // effect rather than adding `meta` to the lifecycle effect's deps array.
  useLayoutEffect(() => {
    if (!active) return undefined;
    register(corner, id, priority, stableMeta);
    return undefined;
  });

  const index = useMemo(() => {
    const i = computeFabStackIndex(priorityMapOf(cornerEntries), id);
    return i < 0 ? 0 : i;
  }, [cornerEntries, id]);

  return {
    index,
    style: active ? { '--fab-stack-index': index } : undefined,
  };
}

/**
 * useFabCornerEntries — read-only view of one corner's current registrants,
 * ordered nearest-the-trigger first (same order useFabStackSlot's `index`
 * numbers by). Used by FabMenu to render the 'menu' corner's items without
 * each item component needing to render its own fixed-position markup.
 *
 * @param {string} corner
 * @returns {Array<{ id: string, priority: number, index: number, meta: object|undefined }>}
 */
export function useFabCornerEntries(corner) {
  const { corners } = useContext(FabStackContext);
  const cornerEntries = corners[corner] || EMPTY_CORNER;
  return useMemo(() => {
    const order = computeFabStackOrder(priorityMapOf(cornerEntries));
    return order.map((id, index) => ({
      id,
      priority: cornerEntries[id].priority,
      index,
      meta: cornerEntries[id].meta,
    }));
  }, [cornerEntries]);
}
